import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import UserUtils from "../../../basecomp/auth/UserUtils";
import {
  updateStatusMCTTableById,
  updateStatusAndData,
  validateData,
} from "../model/MatRequestModel";
import {
  STATUS_ON_HOLD,
  STATUS_APPROVED,
  STATUS_REJECTED,
  STATUS_IN_PROGRESS,
} from "../../assets/Strings";

const formatRejectedComment = (comment, missingText = "") => {
  if (!comment) return comment;

  const prefix = /^Kindly Update the\s+/i;
  const suffix = /\s+and\s+resubmit the request\.?$/i;

  // Not the expected format, or already formatted: leave it alone
  if (!prefix.test(comment) || !suffix.test(comment)) return comment;
  if (/,\s*add\s/i.test(comment)) return comment;

  const middle = comment.replace(prefix, "").replace(suffix, "");

  // "Mandatory fields missing: UOM, Plant" -> ["uom", "plant"]
  const missing = (missingText.split(":")[1] || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const parts = middle
    .split(/\s+and\s+/i)
    .map((s) => s.trim())
    .filter(Boolean);

  const updates = parts.filter((p) => !missing.includes(p.toLowerCase()));
  const adds = parts.filter((p) => missing.includes(p.toLowerCase()));

  const segments = [];
  if (updates.length) segments.push(`Update the ${updates.join(" and ")}`);
  if (adds.length) segments.push(`add ${adds.join(" and ")}`);

  return `Kindly ${segments.join(", ")} and resubmit the request`;
};

const applyCommentFormat = (data) => {
  const field = data?.rejected_request_comments;
  if (!field) return data;
  return {
    ...data,
    rejected_request_comments: {
      ...field,
      value: formatRejectedComment(field.value, field.initialValue),
    },
  };
};
const MatProcessVM = () => {
  const { getUserName } = UserUtils;
  const navigate = useNavigate();
  const { handleToastOpen } = useToast();
  const { id } = useParams();
  const [processInput, setProcessInput] = useState([]);
  const [validationStatus, setValidationStatus] = useState(false);
  const [selectableDialogOpen, setSelectableDialogOpen] = React.useState(false);
  const [selectableTableData, setSelectableTableData] = React.useState(null);
  const [selectedIntMaterial, setSelectedIntMaterial] = React.useState(null);
  const hasValidatedRef = useRef(false);
  const [hasValidated, setHasValidated] = useState(false);
  const [mpnWarningShown, setMpnWarningShown] = useState(false);
  const stopAlreadySent = useRef(false); // To prevent multiple stop actions
  // // ✅ Track if user is actively working on this request
  const [isWorking, setIsWorking] = useState(false);
  // ✅ Track if component is mounted (to prevent setState on unmounted component)
  const [isMounted, setIsMounted] = useState(true);

  const [buttonJson, setButtonJson] = useState({
    validate: {
      text: "Validate",
      tooltipText: "Validate the Process",
      isDisabled: false,
    },
    approved: {
      text: STATUS_APPROVED,
      tooltipText: "Approve the Request",
      isDisabled: false,
    },
    rejected: {
      text: STATUS_REJECTED,
      tooltipText: "Reject the Request",
      isDisabled: false,
    },
    onhold: {
      text: STATUS_ON_HOLD,
      tooltipText: "On Hold the Request",
      isDisabled: false,
    },
  });

  const location = useLocation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [mpnDialogOpen, setMpnDialogOpen] = useState(false);
  const [mpnEmptyDialogOpen, setMpnEmptyDialogOpen] = useState(false);
  const [tableActualData, setTableActualData] = useState([]);
  const [tableRowData, setTableRowData] = useState([]);
  const [tableHeadData, setTableHeadData] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const [processingModalMessage, setProcessingModalMessage] = useState("");

  const handleMpnEmptyDialogClose = () => {
    setMpnEmptyDialogOpen(false);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleMpnDialogClose = () => {
    setMpnDialogOpen(false);
  };

  const handleMpnDialogOpen = () => {
    setMpnDialogOpen(true);
  };

  const disableAllButtons = () => {
    setButtonJson((prevState) => {
      const updatedButtonJson = {};
      for (const key in prevState) {
        updatedButtonJson[key] = { ...prevState[key], isDisabled: true };
      }
      return updatedButtonJson;
    });
  };

  // ============================================================================
  // ✅ EFFECT 1: Initial Load - START action when opening request
  // ============================================================================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userName = getUserName();

        // ✅ Send "start" action to backend
        const response = await updateStatusMCTTableById(id, "start");

        let data = response.data;

        // ✅ Check if another user is already working on this
        if (response.data.message) {
          if (isMounted) {
            setIsProcessingModalOpen(true);
            setProcessingModalMessage(response.data.message);
          }
          return;
        }

        // ✅ Mark as actively working
        if (isMounted) {
          setIsWorking(true);
        }

        // ✅ If request is already APPROVED or REJECTED, disable all buttons
        if (
          data.status.value === STATUS_APPROVED ||
          data.status.value === STATUS_REJECTED
        ) {
          disableAllButtons();
          if (isMounted) {
            setIsWorking(false);
          }
        } else if (data.status.value === STATUS_ON_HOLD) {
          setButtonJson((prevState) => ({
            ...prevState,
            onhold: {
              ...prevState.onhold,
              isDisabled: true,
            },
          }));
        }

        // ✅ Filter fields based on material type
        if (data["material_number"]?.value?.startsWith("8")) {
          if (data["reason"]) {
            data["reason"].isDisabled = false;
          }

          const allowedFields = [
            "material_number",
            "int_material_no",
            "request_date",
            "description",
            "plant_id",
            "reason",
            "user_name",
            "type_of_request",
            "ind_std_desc",
            "status",
            "po_text",
            "mpn_name",
            "mpn_number",
            "material_type",
            "material_group",
            "approvers_comments",
            "standard_comments",
            "rejected_request_comments",
          ];

          const filteredData = {};
          allowedFields.forEach((field) => {
            if (data[field] !== undefined) {
              filteredData[field] = data[field];
            }
          });

          data = filteredData;
        }

        // ✅ Update mdm_name if exists
        if (data["mdm_name"]) {
          data["mdm_name"].value = userName;
          data["mdm_name"].initialValue = userName;
        }

        if (isMounted) {
          console.log("✅ Fetched request data:", data);
          setProcessInput(applyCommentFormat(data));
        }
      } catch (error) {
        console.error("❌ Error fetching request data:", error);
        if (isMounted) {
          handleToastOpen("error", error.message);
          setIsWorking(false);
        }
      }
    };

    fetchData();

    // ✅ Cleanup on unmount
    return () => {
      setIsMounted(true);
    };
  }, [id]);

  // ============================================================================
  // ✅ EFFECT 2: Page Unload Handler - STOP action on beforeunload
  // ============================================================================
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isWorking) return;

      // ✅ Use sendBeacon for reliability (fires even on page unload)
      try {
        navigator.sendBeacon(
          "/api/mct/updateStatusMCTTableById",
          JSON.stringify({
            id: id,
            action: "stop", // ← Revert status to PENDING
          }),
        );
      } catch (error) {
        console.error("❌ Error sending stop action:", error);
      }

      // ✅ Show confirmation dialog (optional)
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    // ✅ Add listener
    window.addEventListener("beforeunload", handleBeforeUnload);

    // ✅ Cleanup
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isWorking, id]);

  const handleInputChange = (itemId) => (value) => {
    setProcessInput((prevSelected) => {
      const itemObject = prevSelected[itemId];
      const updatedItemObject = {
        ...itemObject,
        value: value,
      };
      return {
        ...prevSelected,
        [itemId]: updatedItemObject,
      };
    });
  };

  const createDebouncedFunction = (func, delay) => {
    let timeoutId;
    let lastArgs;
    let context;

    const executeFunction = () => {
      func.apply(context, lastArgs);
      timeoutId = null;
    };

    return function (...args) {
      context = this;
      lastArgs = args;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(executeFunction, delay);
    };
  };

  const swapMaterialColumns = (tableView) => {
    const headData = tableView.tableHeadData.map((item) => item.value);
    const rowData = [...tableView.tableRowData];
    [headData[1], headData[5]] = [headData[5], headData[1]];
    [rowData[1], rowData[5]] = [rowData[5], rowData[1]];
    return { headData, rowData };
  };

  const handleInfoClick = (tableView) => {
    if (tableView?.isSelectable) {
      // Open selectable dialog (user can pick a row)
      setSelectableTableData(tableView);
      setSelectableDialogOpen(true);
    } else {
      // Existing read-only dialog behaviour
      const transformedHeadData = tableView.tableHeadData.map(
        (item) => item.value,
      );
      const { headData, rowData } = swapMaterialColumns(tableView);
      setTableHeadData(transformedHeadData);
      setTableRowData(tableView.tableRowData);
      setTableActualData(tableView.tableActualData);
      handleDialogOpen();
    }
  };

  const handleRowSelect = (row, tableView) => {
    const selectedValue = row[tableView.selectKey];
    const fieldToPopulate = tableView.onSelectField;

    if (!selectedValue || !fieldToPopulate) return;

    setSelectedIntMaterial(selectedValue);

    setProcessInput((prev) => {
      const materialNumber = prev["material_number"]?.value || "";

      const isGT00 = row.plant_id === "GT00";

      let updatedComment;

      if (isGT00) {
        const requestType = /^[a-zA-Z]/.test(materialNumber)
          ? "Copy"
          : "Extend";

        updatedComment = `This material is already present in GT00 please raise a ${requestType} request with ${selectedValue} material number`;
      } else {
        updatedComment = `This material is already present in your own plant with ${selectedValue} material number`;
      }

      return {
        ...prev,

        [fieldToPopulate]: {
          ...prev[fieldToPopulate],
          value: selectedValue,
          otherType: "success",
          otherMsg: `Selected: ${selectedValue}`,
        },

        rejected_request_comments: {
          ...prev.rejected_request_comments,
          value: updatedComment,
        },
      };
    });

    setSelectableDialogOpen(false);
  };
  // Matches the auto-generated "Kindly update the X and resubmit the request."
  // message produced by buildDynamicRejectionComment for editable-field errors.
  const isAutoValidationComment = (value) => {
    if (!value) return false;
    return /^Kindly update the .+ and resubmit the request\.$/.test(
      value.trim(),
    );
  };

  const handleSelectableDialogClose = () => {
    setSelectableDialogOpen(false);

    setProcessInput((prev) => {
      const currentComment = prev.rejected_request_comments?.value || "";

      // Only clear it if it's the auto-generated validation comment.
      // Any other comment (e.g. the duplicate-MPN message set by
      // handleRowSelect, or something the user typed) stays as-is.
      if (!isAutoValidationComment(currentComment)) {
        return prev;
      }

      return {
        ...prev,
        rejected_request_comments: {
          ...prev.rejected_request_comments,
          value: "",
        },
      };
    });
  };

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    // 1 = first warning, 2 = second confirmation
    newStatus: null,
    keyType: null,
  });

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ open: false, newStatus: null, keyType: null });
  };

  // Called when user clicks Approve / Reject / On Hold button
  const handleConfirmDialogOpen = (keyType, newStatus) => {
    // if (validationStatus === false && newStatus !== STATUS_REJECTED) {
    //   handleToastOpen("error", "Please validate before proceeding");
    //   return;
    // }
    setConfirmDialog({ open: true, newStatus, keyType });
  };

  // Called on first confirm → move to step 2

  // Called on second confirm → actually execute the action
  const handleFinalConfirm = async () => {
    handleConfirmDialogClose();
    await handleButtonClick(confirmDialog.keyType, confirmDialog.newStatus);
  };

  const resetValidation = () => {
    hasValidatedRef.current = false;
    setHasValidated(false);
  };

  const handleButtonClick = async (keyType, newStatus) => {
    if (keyType === "validate") {
      try {
        const response = await validateData(id, processInput, 1);

        if (response.data.errorMessage) {
          handleToastOpen("error", response.data.errorMessage);
        } else if (response.data.message) {
          handleToastOpen("success", response.data.message);
        }

        const inputJson = applyCommentFormat(response.data.inputJson);
        if (response.data.validationStatus === false) {
          const dynamicComment = buildDynamicRejectionComment(inputJson);
          if (dynamicComment && inputJson.rejected_request_comments) {
            inputJson.rejected_request_comments = {
              ...inputJson.rejected_request_comments,
              value: dynamicComment,
            };
          }
        }

        if (isMounted) {
          setProcessInput(inputJson);
          setValidationStatus(response.data.validationStatus);
        }
        if (response.data.validationStatus === true) {
          setHasValidated(true); // ← must come AFTER setProcessInput
          setButtonJson((prev) => ({
            ...prev,
            approved: { ...prev.approved, isDisabled: false },
            rejected: { ...prev.rejected, isDisabled: false },
            onhold: { ...prev.onhold, isDisabled: false },
          }));
        }

        if (
          Array.isArray(
            response.data?.inputJson?.mpn_number?.duplicateMpnTableView
              ?.tableActualData,
          ) &&
          response.data.inputJson.mpn_number.duplicateMpnTableView
            .tableActualData.length > 0
        ) {
          const duplicateMpnTableView =
            response.data.inputJson.mpn_number.duplicateMpnTableView;
          const transformedHeadData = duplicateMpnTableView.tableHeadData.map(
            (item) => item.value,
          );
          if (isMounted) {
            setTableHeadData(transformedHeadData);
            setTableRowData(duplicateMpnTableView.tableRowData);
            setTableActualData(duplicateMpnTableView.tableActualData);
            handleMpnDialogOpen();
          }
        }

        if (
          Array.isArray(
            response.data?.inputJson?.mpn_number?.tableViewData
              ?.tableActualData,
          ) &&
          response.data.inputJson.mpn_number.tableViewData.tableActualData
            .length > 0
        ) {
          const tableViewData =
            response.data.inputJson.mpn_number.tableViewData;
          if (isMounted) {
            setSelectableTableData(tableViewData);
            setSelectableDialogOpen(true);
            setSelectedIntMaterial(null);
          }
        }
      } catch (error) {
        const backendMessage = error?.response?.data?.message || error.message;
        if (backendMessage === "MPN is blank.") {
          setMpnEmptyDialogOpen(true);
        } else {
          handleToastOpen("error", backendMessage);
        }
      }
      return;
    }

    let isError = false;
    const newInput = [];

    for (const key in processInput) {
      const item = processInput[key];

      // Safely get the value — default to empty string if undefined/null
      const itemValue = item.value != null ? String(item.value) : "";

      // if (item.isRequired && itemValue.trim() === "") {
      //   handleToastOpen(
      //     "error",
      //     `Field "${item.enter}" is required but the value is blank.`,
      //   );
      //   isError = true;
      //   break;
      // }

      newInput.push({ [key]: itemValue });
    }

    if (newStatus === STATUS_REJECTED) {
      // const comments = processInput["rejected_request_comments"];
      // const commentsValue = comments?.value != null ? String(comments.value) : "";
      const commentsValue =
        processInput["rejected_request_comments"]?.value != null
          ? String(processInput["rejected_request_comments"].value)
          : "";
      if (commentsValue.trim() === "") {
        handleToastOpen(
          "error",
          `Field rejected_request_comments is required in case of rejecting the query.`,
        );
        isError = true;
      }
    }

    if (!isError) {
      try {
        const userName = getUserName();
        const response = await updateStatusAndData(
          id,
          newInput,
          newStatus,
          userName,
        );

        if (isMounted) {
          setIsWorking(false);
          disableAllButtons();
        }

        handleToastOpen("success", response.data.message);

        if (
          newStatus === STATUS_APPROVED ||
          newStatus === STATUS_REJECTED ||
          newStatus === STATUS_ON_HOLD
        ) {
          setTimeout(() => {
            navigate("/material-creation/request-tracker");
          }, 500);
        }
      } catch (error) {
        console.error("❌ Error updating status:", error);
        handleToastOpen("error", error.message);
      }
    }
  };
  const handleBackButtonClick = () => {
    stopAlreadySent.current = true;
    setIsWorking(false);

    updateStatusMCTTableById(id, "stop").catch(console.error);

    navigate("/material-creation/request-tracker");
  };

  const handleModalClose = () => {
    if (isMounted) {
      setIsProcessingModalOpen(false);
      setIsWorking(false);
    }
    navigate(-1);
  };

  const FIELD_ORDER = [
    "material_number",
    "plant_id",
    "request_date",
    "type_of_request",
    "reason",
    "user_name",
    "description",
    "ind_std_desc",
    "status",
    "long_description",
    "po_text",
    "b_un",
    "mpn_name",
    "mpn_number",
    "abc_indicator",
    "material_type",
    "level1",
    "mrp_type",
    "material_group",
    "level2",
    "reorder_point",
    "part_type_code",
    "level3",
    "lot_size",
    "purchase_group",
    "level4",
    "max_stock_level",
    "valuation_class",
    "storage_bin",
    "fixed_lot_size",
    "lead_time",
    "equipment_function",
    "material_criticality",
  ];

  // Fields that CANNOT be edited on an existing SAP request — these require a fresh request
  const NON_EDITABLE_FIELDS = new Set(["material_type", "valuation_class"]);

  // const buildDynamicRejectionComment = (inputJson) => {
  //   const existingComment = inputJson["rejected_request_comments"]?.value;

  //   // Keep the backend-generated comment if it already exists
  //   if (existingComment && existingComment.trim() !== "") {
  //     return existingComment;
  //   }

  //   const errorKeys = Object.keys(inputJson).filter(
  //     (key) =>
  //       inputJson[key]?.otherType === "error" && inputJson[key]?.otherMsg,
  //   );

  //   errorKeys.sort((a, b) => {
  //     const indexA = FIELD_ORDER.indexOf(a);
  //     const indexB = FIELD_ORDER.indexOf(b);

  //     if (indexA === -1 && indexB === -1) return 0;
  //     if (indexA === -1) return 1;
  //     if (indexB === -1) return -1;

  //     return indexA - indexB;
  //   });

  //   const editableFields = errorKeys.filter((k) => !NON_EDITABLE_FIELDS.has(k));

  //   const nonEditableFields = errorKeys.filter((k) =>
  //     NON_EDITABLE_FIELDS.has(k),
  //   );

  //   const messages = [];

  //   if (editableFields.length > 0) {
  //     const fieldList = editableFields
  //       .map((k) => `correct ${inputJson[k].enter}`)
  //       .join(", ");

  //     messages.push(`Kindly update the ${fieldList} and resubmit the request 1.`);
  //   }

  //   if (nonEditableFields.length > 0) {
  //     // One combined sentence, each field shown as "<label> as \"<current value>\""
  //     const fieldList = nonEditableFields
  //       .map((k) => `correct ${inputJson[k].enter} as "${inputJson[k].value ?? ""}"`)
  //       .join(", ");

  //     messages.push(`Kindly raise new creation request with ${fieldList}.`);
  //   }

  //   return messages.join(" ");
  // };

  const buildDynamicRejectionComment = (inputJson) => {
    const existingComment = inputJson["rejected_request_comments"]?.value;

    // Keep the backend-generated comment if it already exists
    if (existingComment && existingComment.trim() !== "") {
      return existingComment;
    }

    // Do not show a rejection comment for COPY requests
    // when the validation error is "Ind Std Desc not found"
    const isCopyRequest = inputJson["type_of_request"]?.value === "COPY";

    const hasIndStdDescError = Object.values(inputJson).some(
      (field) =>
        field?.otherType === "error" &&
        field?.otherMsg?.trim() === "Ind Std Desc not found",
    );

    if (isCopyRequest && hasIndStdDescError) {
      return "";
    }

    const errorKeys = Object.keys(inputJson).filter(
      (key) =>
        inputJson[key]?.otherType === "error" && inputJson[key]?.otherMsg,
    );

    errorKeys.sort((a, b) => {
      const indexA = FIELD_ORDER.indexOf(a);
      const indexB = FIELD_ORDER.indexOf(b);

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    const editableFields = errorKeys.filter((k) => !NON_EDITABLE_FIELDS.has(k));

    const nonEditableFields = errorKeys.filter((k) =>
      NON_EDITABLE_FIELDS.has(k),
    );

    const messages = [];

    if (editableFields.length > 0) {
      const fieldList = editableFields
        .map((k) => `correct ${inputJson[k].enter}`)
        .join(", ");

      messages.push(
        `Kindly update the ${fieldList} and resubmit the request .`,
      );
    }

    if (nonEditableFields.length > 0) {
      const fieldList = nonEditableFields
        .map(
          (k) =>
            `correct ${inputJson[k].enter} as "${inputJson[k].value ?? ""}"`,
        )
        .join(", ");

      messages.push(`Kindly raise new creation request with ${fieldList}.`);
    }

    return messages.join(" ");
  };

  return {
    processInput,
    setProcessInput,
    handleInputChange,
    handleInfoClick,
    handleBackButtonClick,
    buttonJson,
    handleButtonClick,
    handleConfirmDialogOpen,
    confirmDialog,
    handleConfirmDialogClose,

    handleFinalConfirm,
    dialogOpen,
    mpnDialogOpen,
    handleMpnDialogClose,
    handleDialogClose,
    tableActualData,
    tableRowData,
    tableHeadData,
    modalData,
    setModalData,
    isProcessingModalOpen,
    handleModalClose,
    processingModalMessage,
    // partialMpnDialogOpen,
    // handlePartialMpnDialogClose,
    // partialMpnTableData,
    isWorking, // ✅ Export for UI to show loading states if needed
    selectableDialogOpen,
    // handleSelectableDialogClose: () => setSelectableDialogOpen(false),
    handleSelectableDialogClose,
    handleRowSelect,
    selectableTableData,
    selectedIntMaterial,
    hasValidated,
    resetValidation,
    mpnEmptyDialogOpen,
    handleMpnEmptyDialogClose,
  };
};

export default MatProcessVM;
